/**
 * Move coin the custodian is holding under individual customers back into the master balance.
 *
 * ## Why an operator needs this
 *
 * With a per-customer custody ledger, coin arrives into the sub-account of whoever deposited
 * it. The master balance is the one the platform can actually spend from - it funds payouts,
 * conversions and withdrawals - and nothing moves coin between the two on its own, except the
 * write-off a conversion queues for exactly what it consumed.
 *
 * So the platform can hold a real balance at the custodian and still refuse a crypto buy for
 * want of coin it already owns, because every coin is filed under a customer. This page is how
 * that gets fixed without anybody opening a vendor dashboard.
 *
 * ## What the button does, and does not
 *
 * It **queues** a write-off per holding. The movements run afterwards, one at a time, each
 * asking the custodian what it actually holds before debiting anything and refusing an asset
 * held on two chains rather than guessing. So the count here is instructions accepted, not
 * money moved, and the wording says so.
 *
 * It does **not** touch customer balances. A write-off changes where the custodian files our
 * coin; what a customer is owed is ours and is untouched.
 */
import { useCallback, useEffect, useState } from 'react';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import type { CustodySweepPreview, CustodySweepResult } from '../contracts/types.js';
import { Badge, Button, Card, ErrorBox, LoadingState } from '../components/ui.js';

/** Why a balance was found but left where it is, in an operator's words. */
const SKIP_REASONS: Readonly<Record<string, string>> = {
  unknown_ticker: 'We do not recognise that ticker, so moving it would risk debiting the wrong balance.',
  not_positive: 'Nothing to move.',
  unreadable_amount: 'The amount could not be read exactly, and an approximate transfer is not worth making.',
};

export function TreasuryCustodySweepPage(): JSX.Element {
  const { api } = useAuth();
  const [preview, setPreview] = useState<CustodySweepPreview | null>(null);
  const [result, setResult] = useState<CustodySweepResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPreview(await api.custodySweepPreview());
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  async function sweep(): Promise<void> {
    setRunning(true);
    setError(null);
    try {
      setResult(await api.custodySweep());
      // Re-read rather than assume the list is now empty: the movements have only been queued,
      // and what the custodian holds is its answer to give, not ours to predict.
      await load();
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setRunning(false);
    }
  }

  const holdings = preview?.holdings ?? [];

  return (
    <div>
      <header className="page-head">
        <h1 className="page-head__title">Move coin to the main account</h1>
        <p className="page-head__sub">
          Your custodian files each deposit under the customer who made it. Only the main
          balance can fund payouts, conversions and withdrawals, so coin left in customer
          accounts cannot be spent. This moves it across.
        </p>
      </header>

      {error !== null && (
        <div style={{ marginBottom: 16 }}>
          <ErrorBox message={error} />
        </div>
      )}

      {result !== null && (
        <div style={{ marginBottom: 16 }}>
          <Card title="Sent">
            <p>
              {result.queued === 0
                ? 'Nothing was queued.'
                : `${String(result.queued)} transfer${result.queued === 1 ? '' : 's'} queued at ${result.provider}.`}
            </p>
            <p className="muted">
              Queued, not finished. Each one asks {result.provider} what it is holding before it
              moves anything, so check back here in a minute to see what is left.
            </p>
          </Card>
        </div>
      )}

      <Card
        title="In customer accounts"
        actions={
          preview === null ? undefined : <Badge tone="neutral">{preview.provider}</Badge>
        }
      >
        {loading ? (
          <LoadingState label="Asking your custodian what it is holding…" />
        ) : holdings.length === 0 ? (
          <p className="muted">
            Nothing is sitting in a customer account. Everything your custodian holds is already
            in the main balance.
          </p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Asset</th>
                <th>Network</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th>Customer account</th>
              </tr>
            </thead>
            <tbody>
              {holdings.map((holding) => (
                <tr key={`${holding.subAccountId}-${holding.currency}-${holding.network}`}>
                  <td>{holding.currency}</td>
                  <td>{holding.network}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {holding.amount}
                  </td>
                  <td className="mono">{holding.subAccountId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div style={{ marginTop: 16 }}>
          <Button
            variant="primary"
            loading={running}
            disabled={loading || holdings.length === 0}
            onClick={() => void sweep()}
          >
            {running ? 'Sending…' : 'Move it all to the main account'}
          </Button>
        </div>
      </Card>

      {(preview?.skipped.length ?? 0) > 0 && (
        <div style={{ marginTop: 20 }}>
          <Card title="Left where it is">
            {/* Shown rather than dropped: a balance that silently disappears from this page
                looks like a zero, and an operator would go looking for it at the vendor. */}
            <table className="table">
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th>Why</th>
                </tr>
              </thead>
              <tbody>
                {(preview?.skipped ?? []).map((entry) => (
                  <tr key={`${entry.subAccountId}-${entry.ticker}`}>
                    <td className="mono">{entry.ticker}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>
                      {entry.amount}
                    </td>
                    <td>{SKIP_REASONS[entry.reason] ?? entry.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {(preview?.unreadable.length ?? 0) > 0 && (
        <div style={{ marginTop: 20 }}>
          <Card title="Could not be checked">
            <p className="muted">
              Your custodian would not tell us what these customer accounts are holding, so they
              were left alone. That is worth chasing: a balance nobody can read is a balance
              nobody can spend.
            </p>
            <ul>
              {(preview?.unreadable ?? []).map((id) => (
                <li key={id} className="mono">
                  {id}
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}
    </div>
  );
}
